import * as d3 from 'd3';

export function createComputerClassGraph(container) {
  const graphWidth = 500;
  const graphHeight = 500;
  const axisMargin = 60;
  const svgWidth = graphWidth + axisMargin + 30;
  const svgHeight = graphHeight + axisMargin;

  // Create SVG
  const svg = d3.select(container)
    .append('svg')
    .attr('width', svgWidth)
    .attr('height', svgHeight);

  // Scales
  const xDomain = [1976, 1986];
  const xScale = d3.scaleLinear()
    .domain(xDomain)
    .range([0, graphWidth]);

  const yDomain = [100, 120000];
  const yScale = d3.scaleLog()
    .base(10)
    .domain(yDomain)
    .range([graphHeight, 0]);

  // Data
  const data = [
    { make: "IBM", model: "PC", price: 1600, year: 1981, emphasized: true },
    { make: "IBM", model: "AT", price: 6000, year: 1984 },
    { make: "IBM", model: "XT", price: 4400, year: 1983 },
    { make: "Apple", model: "II", price: 1300, year: 1977 },
    { make: "Apple", model: "Macintosh", price: 2500, year: 1984 },
    { make: "Tandy", model: "TRS-80", price: 400, year: 1977 },
    { make: "Commodore", model: "PET", price: 800, year: 1977 },
    { make: "Commodore", model: "64", price: 600, year: 1982 },
    { make: "Atari", model: "800", price: 1000, year: 1979 },
    { make: "Sun Microsystems", model: "Sun-1", price: 8900, year: 1982 },
    { make: "Sun Microsystems", model: "Sun-2", price: 12000, year: 1983 },
    { make: "Sun Microsystems", model: "Sun-3", price: 16000, year: 1985, labelSide: "left" },
    { make: "Apollo", model: "DN100", price: 25000, year: 1981 },
    { make: "Symbolics", model: "LM-2", price: 100000, year: 1981 },
    { make: "Xerox", model: "Star", price: 75000, year: 1981, emphasized: true },
    { make: "SGI", model: "IRIS 1400", price: 60000, year: 1984 }
  ];

  const sectorLabels = [
    ["Home Computers", 1977, 200],
    ["Personal Computers", 1977, 3000],
    ["Workstations", 1977, 40000],
  ];

  // Create axes
  const xAxis = d3.axisBottom(xScale)
    .tickFormat(d => d);

  const yAxis = d3.axisLeft(yScale)
    .tickFormat(num => {
      if ((Math.log10(Math.abs(num)) % 1) !== 0) return "";
      
      const SI_SYMBOL = ["", "k", "M", "G", "T", "P", "E"];
      const tier = Math.log10(Math.abs(num)) / 3 | 0;
      return num / Math.pow(10, tier * 3) + SI_SYMBOL[tier];
    });

  // Add Y axis
  svg.append('g')
    .attr('class', 'y-axis')
    .attr('transform', `translate(${axisMargin}, 0)`)
    .call(yAxis)
    .style('font-family', 'inherit');

  // Style Y axis lines and ticks
  svg.select('.y-axis')
    .selectAll('line')
    .style('stroke', '#000');

  svg.select('.y-axis')
    .select('path')
    .style('stroke', '#000');

  // Style Y axis text
  svg.select('.y-axis')
    .selectAll('text')
    .style('fill', '#000');

  // Add Y axis label
  svg.append('text')
    .attr('transform', 'rotate(-90)')
    .attr('y', 15)
    .attr('x', 0 - (graphHeight / 2))
    .style('text-anchor', 'middle')
    .style('font-family', 'inherit')
    .style('fill', '#000')
    .text('Price ($)');

  // Add X axis
  svg.append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(${axisMargin}, ${graphHeight})`)
    .call(xAxis)
    .style('font-family', 'inherit');

  // Style X axis lines and ticks
  svg.select('.x-axis')
    .selectAll('line')
    .style('stroke', '#000');

  svg.select('.x-axis')
    .select('path')
    .style('stroke', '#000');

  // Style X axis text
  svg.select('.x-axis')
    .selectAll('text')
    .style('fill', '#000');

  // Add X axis label
  svg.append('text')
    .attr('transform', `translate(${axisMargin + graphWidth / 2}, ${graphHeight + axisMargin - 10})`)
    .style('text-anchor', 'middle')
    .style('font-family', 'inherit')
    .style('fill', '#000')
    .text('Introduction year');

  // Create main group
  const mainGroup = svg.append('g')
    .attr('transform', `translate(${axisMargin}, 0)`);

  // Add sector divider lines
  const sectorDividerStyle = {
    'stroke-width': 1,
    'stroke': '#000',
    'stroke-dasharray': '4'
  };

  // Horizontal divider lines
  mainGroup.append('line')
    .attr('x1', xScale(1976))
    .attr('y1', yScale(1450))
    .attr('x2', xScale(1986))
    .attr('y2', yScale(1450))
    .style('stroke-width', sectorDividerStyle['stroke-width'])
    .style('stroke', sectorDividerStyle['stroke'])
    .style('stroke-dasharray', sectorDividerStyle['stroke-dasharray']);

  mainGroup.append('line')
    .attr('x1', xScale(1976))
    .attr('y1', yScale(7000))
    .attr('x2', xScale(1986))
    .attr('y2', yScale(7000))
    .style('stroke-width', sectorDividerStyle['stroke-width'])
    .style('stroke', sectorDividerStyle['stroke'])
    .style('stroke-dasharray', sectorDividerStyle['stroke-dasharray']);

  // Add sector labels
  sectorLabels.forEach(d => {
    mainGroup.append('text')
      .attr('class', 'sector-label')
      .attr('x', xScale(d[1]))
      .attr('y', yScale(d[2]))
      .style('font-family', 'inherit')
      .text(d[0]);
  });

  // Add data points
  data.forEach(d => {
    const labelOffset = d.labelOffset || [5, 4];
    const labelText = d.make + " " + d.model;
    const isLeftSide = d.labelSide === "left";

    // Calculate label position
    const labelX = isLeftSide ? xScale(d.year) - 5 : xScale(d.year) + labelOffset[0];
    const labelY = yScale(d.price) + labelOffset[1];

    // Add circle
    mainGroup.append('circle')
      .attr('cx', xScale(d.year))
      .attr('cy', yScale(d.price))
      .attr('r', 3)
      .style('fill', 'black')
      .style('stroke', '#fff')
      .style('stroke-width', 1);

    // Add label
    const label = mainGroup.append('text')
      .attr('class', d.emphasized ? 'data-label emphasized' : 'data-label')
      .attr('x', labelX)
      .attr('y', labelY)
      .style('font-family', 'inherit')
      .style('text-anchor', isLeftSide ? 'end' : 'start')
      .style('text-shadow', '-1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff')
      .text(labelText);

    // Apply emphasis styles if needed
    if (d.emphasized) {
      label.style('text-shadow', '-1px -1px 0 yellow, 1px -1px 0 yellow, -1px 1px 0 yellow, 1px 1px 0 yellow')
           .style('font-size', '1rem');
    }
  });
} 